import React, { useEffect, useRef } from 'react';

// The hero's illustrative city: a tilted block grid with a river, a "You" pin
// in the middle and preview connections arcing out to nearby talent. Everything is
// drawn on one canvas; the buildings are painted once per size and each frame
// only adds the moving parts on top. Ported from the Haastia Discovery design.
//
// On wide screens the map fills the hero behind the headline, as designed. On
// phones (the same breakpoint as discovery.css) it is a band below the copy, so
// the pin and the preview cards never land on the text or the Join button.

const STACKED_QUERY = '(max-width: 859px)';

const SPEED = 1;
const MAX_TRIPS = 4;
const MAX_TRIPS_STACKED = 2;
const BLOCK = 90;
const STREET = 18;
const BLOCKS_PER_SIDE = 30;
const PIN_HEIGHT = 52;
const SERVICES = [
  ['Knotless braids', '2.1 mi'], ['Gel-X full set', '0.8 mi'], ['Lash fill', '1.4 mi'],
  ['Bridal makeup', '3.0 mi'], ['Silk press', '3.2 mi'], ['Facial at home', '1.9 mi'],
  ['Brow lamination', '2.6 mi'], ['Loc retwist', '0.6 mi'], ['Prom glam', '1.2 mi'],
  ['Mobile pedicure', '2.4 mi'], ['Volume lashes', '1.1 mi'], ['Kids braids', '1.7 mi'],
];

const seededRandom = (seed) => {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

const riverAt = (x) => 330 + Math.sin(x / 430) * 170 - x * 0.22;

const createScene = (canvas) => {
  const scene = { trips: [], dests: [], lastLaunch: -1e9 };

  const proj = (x, y, z = 0) => {
    const c = scene.cam;
    const cs = Math.cos(c.yaw);
    const sn = Math.sin(c.yaw);
    const xr = x * cs - y * sn;
    const yr = x * sn + y * cs;
    const st = Math.sin(c.tilt);
    const ct = Math.cos(c.tilt);
    const d = c.C - yr * st - z * ct;
    const sc = c.C / d;
    return { x: c.cx + xr * sc * c.k, y: c.cy + (yr * ct - z * st) * sc * c.k, s: sc * c.k, d };
  };

  const poly = (g, pts, fill, stroke) => {
    g.beginPath();
    pts.forEach((p, i) => (i ? g.lineTo(p.x, p.y) : g.moveTo(p.x, p.y)));
    g.closePath();
    if (fill) { g.fillStyle = fill; g.fill(); }
    if (stroke) { g.strokeStyle = stroke; g.lineWidth = 0.6; g.stroke(); }
  };

  const roundRect = (ctx, x, y, w, h, r) => {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
  };

  const groundRing = (ctx, x, y, r, color, lineWidth) => {
    ctx.beginPath();
    for (let i = 0; i <= 36; i += 1) {
      const a = (i / 36) * Math.PI * 2;
      const p = proj(x + Math.cos(a) * r, y + Math.sin(a) * r);
      if (i) ctx.lineTo(p.x, p.y); else ctx.moveTo(p.x, p.y);
    }
    ctx.strokeStyle = color;
    ctx.lineWidth = lineWidth;
    ctx.stroke();
  };

  const buildStatic = () => {
    const { w, h, dpr } = scene;
    const R = seededRandom(11);
    const c = document.createElement('canvas');
    c.width = w * dpr;
    c.height = h * dpr;
    const g = c.getContext('2d');
    g.scale(dpr, dpr);
    g.fillStyle = '#f7efe4';
    g.fillRect(0, 0, w, h);
    const P = (x, y, z) => proj(x, y, z);
    const onScreen = (p) => p.x > -160 && p.x < w + 160 && p.y > -160 && p.y < h + 160 && p.d > 200;
    const E = BLOCKS_PER_SIDE * BLOCK;
    poly(g, [P(-E, -E), P(E, -E), P(E, E), P(-E, E)], '#e9dccb');

    const bank = (halfWidth, fill) => {
      const left = [];
      const right = [];
      for (let x = -E; x <= E; x += 30) {
        const y = riverAt(x);
        left.push(P(x, y - halfWidth));
        right.unshift(P(x, y + halfWidth));
      }
      poly(g, left.concat(right), fill);
    };
    bank(46, '#d3dccf');
    bank(30, '#c7d3c6');

    const items = [];
    scene.blocks = [];
    for (let i = -BLOCKS_PER_SIDE; i < BLOCKS_PER_SIDE; i += 1) {
      for (let j = -BLOCKS_PER_SIDE; j < BLOCKS_PER_SIDE; j += 1) {
        const x0 = i * BLOCK + STREET / 2;
        const y0 = j * BLOCK + STREET / 2;
        const sz = BLOCK - STREET;
        const cx = x0 + sz / 2;
        const cy = y0 + sz / 2;
        if (Math.abs(cy - riverAt(cx)) < 92) continue;
        if (!onScreen(P(cx, cy))) continue;
        const park = R() < 0.11 && !(i === 0 && j === 0);
        poly(g, [P(x0, y0), P(x0 + sz, y0), P(x0 + sz, y0 + sz), P(x0, y0 + sz)], park ? '#dfe5c6' : '#f6eee2');
        scene.blocks.push({ i, j });
        if (park) {
          const trees = 4 + Math.floor(R() * 5);
          for (let t = 0; t < trees; t += 1) {
            items.push({ type: 'tree', x: x0 + 8 + R() * (sz - 16), y: y0 + 8 + R() * (sz - 16), r: 7 + R() * 6 });
          }
          continue;
        }
        const lots = R() < 0.5 ? [[0, 0, 1, 1]] : [[0, 0, 0.5, 0.5], [0.5, 0, 1, 0.5], [0, 0.5, 0.5, 1], [0.5, 0.5, 1, 1]];
        lots.forEach(([a1, b1, a2, b2]) => {
          if (lots.length > 1 && R() < 0.18) return;
          const inset = 5;
          const bx0 = x0 + a1 * sz + inset;
          const by0 = y0 + b1 * sz + inset;
          const bx1 = x0 + a2 * sz - inset;
          const by1 = y0 + b2 * sz - inset;
          const ht = 10 + Math.pow(R(), 2.2) * (lots.length > 1 ? 55 : 80);
          const roll = R();
          const roof = roll < 0.12 ? '#f2c1a9' : roll < 0.18 ? '#ccd5b0' : '#fbf7f1';
          items.push({ type: 'box', x0: bx0, y0: by0, x1: bx1, y1: by1, ht, roof });
          const sx = ht * 0.55;
          const sy = ht * 0.35;
          poly(g, [P(bx0, by0), P(bx1, by0), P(bx1 + sx, by0 + sy), P(bx1 + sx, by1 + sy), P(bx0 + sx, by1 + sy), P(bx0, by1)], 'rgba(120,96,70,0.09)');
        });
      }
    }

    const cs = Math.cos(scene.cam.yaw);
    const sn = Math.sin(scene.cam.yaw);
    items.forEach((it) => {
      const cx = it.type === 'box' ? (it.x0 + it.x1) / 2 : it.x;
      const cy = it.type === 'box' ? (it.y0 + it.y1) / 2 : it.y;
      it.d = P(cx, cy).d;
    });
    items.sort((a, b) => b.d - a.d);
    items.forEach((it) => {
      if (it.type === 'tree') {
        const p = P(it.x, it.y, 0);
        const top = P(it.x, it.y, it.r * 1.4);
        g.fillStyle = 'rgba(91,107,67,0.18)';
        g.beginPath(); g.ellipse(p.x + 3 * p.s, p.y, it.r * p.s, it.r * p.s * 0.45, 0, 0, Math.PI * 2); g.fill();
        g.fillStyle = '#adba8d';
        g.beginPath(); g.arc(top.x, top.y, it.r * top.s, 0, Math.PI * 2); g.fill();
        g.fillStyle = '#ccd5b0';
        g.beginPath(); g.arc(top.x - it.r * top.s * 0.3, top.y - it.r * top.s * 0.3, it.r * top.s * 0.5, 0, Math.PI * 2); g.fill();
        return;
      }
      const { x0, y0, x1, y1, ht } = it;
      const corners = [[x0, y0], [x1, y0], [x1, y1], [x0, y1]];
      const normals = [[0, -1], [1, 0], [0, 1], [-1, 0]];
      for (let f = 0; f < 4; f += 1) {
        const [nx, ny] = normals[f];
        if (nx * sn + ny * cs <= 0) continue;
        const a = corners[f];
        const b = corners[(f + 1) % 4];
        const lit = nx * cs - ny * sn < 0;
        poly(g, [P(a[0], a[1], 0), P(b[0], b[1], 0), P(b[0], b[1], ht), P(a[0], a[1], ht)], lit ? '#ecdfcf' : '#d9c6b0', 'rgba(100,84,64,0.16)');
      }
      poly(g, corners.map((q) => P(q[0], q[1], ht)), it.roof, 'rgba(100,84,64,0.2)');
    });

    // Fade the city into the page: behind the headline on wide screens, into
    // the copy above the band on phones, and along the bottom edge.
    const fogHeight = h * (scene.narrow ? 0.3 : 0.5);
    const fog = g.createLinearGradient(0, 0, 0, fogHeight);
    fog.addColorStop(0, 'rgba(247,239,228,1)');
    fog.addColorStop(0.35, 'rgba(247,239,228,0.75)');
    fog.addColorStop(1, 'rgba(247,239,228,0)');
    g.fillStyle = fog;
    g.fillRect(0, 0, w, fogHeight);
    if (!scene.narrow) {
      const fade = g.createLinearGradient(0, 0, w * 0.6, 0);
      fade.addColorStop(0, 'rgba(247,239,228,0.97)');
      fade.addColorStop(0.55, 'rgba(247,239,228,0.85)');
      fade.addColorStop(1, 'rgba(247,239,228,0)');
      g.fillStyle = fade;
      g.fillRect(0, 0, w * 0.6, h);
    }
    const bottom = g.createLinearGradient(0, h - 110, 0, h);
    bottom.addColorStop(0, 'rgba(247,239,228,0)');
    bottom.addColorStop(1, 'rgba(247,239,228,0.95)');
    g.fillStyle = bottom;
    g.fillRect(0, h - 110, w, 110);
    scene.staticCanvas = c;
  };

  const buildDests = () => {
    const { w, h, narrow } = scene;
    const R = seededRandom(21);
    const dests = [];
    const pin = proj(0, 0);
    const candidates = scene.blocks.slice().sort(() => R() - 0.5);
    for (const block of candidates) {
      if (dests.length >= SERVICES.length) break;
      const x = block.i * BLOCK;
      const y = block.j * BLOCK;
      if (Math.abs(y - riverAt(x)) < 60) continue;
      const p = proj(x, y);
      if (narrow) {
        // The band has no text over it; keep pins clear of its faded top and the edges.
        if (p.x < 40 || p.x > w - 40 || p.y < h * 0.3 || p.y > h - 50) continue;
        if (Math.hypot(p.x - pin.x, p.y - pin.y) < 90) continue;
        if (dests.some((q) => Math.hypot(q.sx - p.x, q.sy - p.y) < 80)) continue;
      } else {
        // Keep pins off the headline, in the right half of the hero.
        if (p.x < w * 0.5 || (p.y < h * 0.72 && p.x - 120 < Math.min(w * 0.6, 680))) continue;
        if (p.x < 110 || p.x > w - 110 || p.y < 210 || p.y > h - 60) continue;
        if (Math.hypot(p.x - pin.x, p.y - pin.y) < 150) continue;
        if (dests.some((q) => Math.hypot(q.sx - p.x, q.sy - p.y) < 140)) continue;
      }
      const [service, dist] = SERVICES[dests.length];
      dests.push({ x, y, sx: p.x, sy: p.y, service, dist, busy: false });
    }
    scene.dests = dests;
  };

  const resize = () => {
    const rect = canvas.getBoundingClientRect();
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = Math.max(1, rect.width);
    const h = Math.max(1, rect.height);
    if (w === scene.w && h === scene.h) return false;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    const narrow = window.matchMedia?.(STACKED_QUERY).matches ?? w < 860;
    Object.assign(scene, { w, h, dpr, narrow });
    const k = narrow ? Math.max(0.38, w / 1150) : Math.min(w / 1900, h / 1100);
    scene.cam = { cx: narrow ? w * 0.5 : w * 0.66, cy: narrow ? h * 0.56 : h * 0.6, k, yaw: -0.5, tilt: 0.72, C: 2600 };
    buildStatic();
    buildDests();
    scene.trips = [];
    return true;
  };

  const launch = (now) => {
    const free = scene.dests.filter((d) => !d.busy);
    if (!free.length) return;
    const d = free[Math.floor(Math.random() * free.length)];
    d.busy = true;
    const length = Math.hypot(d.x, d.y);
    scene.trips.push({
      d,
      start: now,
      dur: (1000 + length * 2.2) / SPEED,
      side: (Math.random() < 0.5 ? -1 : 1) * (0.03 + Math.random() * 0.05),
    });
  };

  const drawYouPin = (ctx) => {
    const g0 = proj(0, 0, 0);
    const top = proj(0, 0, PIN_HEIGHT);
    const rr = 30;
    ctx.fillStyle = 'rgba(46,43,37,0.2)';
    ctx.beginPath(); ctx.ellipse(g0.x, g0.y, 20 * g0.s, 8 * g0.s, 0, 0, Math.PI * 2); ctx.fill();
    ctx.strokeStyle = '#414d2f';
    ctx.lineWidth = 3;
    ctx.beginPath(); ctx.moveTo(g0.x, g0.y); ctx.lineTo(top.x, top.y + rr); ctx.stroke();
    ctx.save();
    ctx.shadowColor = 'rgba(46,43,37,0.25)'; ctx.shadowBlur = 20; ctx.shadowOffsetY = 8;
    ctx.fillStyle = '#414d2f';
    ctx.beginPath(); ctx.arc(top.x, top.y, rr, 0, Math.PI * 2); ctx.fill();
    ctx.restore();
    ctx.strokeStyle = '#f9f4ed';
    ctx.lineWidth = 4;
    ctx.beginPath(); ctx.arc(top.x, top.y, rr, 0, Math.PI * 2); ctx.stroke();
    ctx.save();
    ctx.beginPath(); ctx.arc(top.x, top.y, rr - 2, 0, Math.PI * 2); ctx.clip();
    ctx.fillStyle = '#f9f4ed';
    ctx.beginPath(); ctx.arc(top.x, top.y - 7, 9, 0, Math.PI * 2); ctx.fill();
    ctx.beginPath(); ctx.ellipse(top.x, top.y + 22, 17, 15, 0, 0, Math.PI * 2); ctx.fill();
    ctx.restore();
    ctx.font = '700 13px Figtree, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const tagWidth = ctx.measureText('You').width + 26;
    ctx.fillStyle = '#201e1d';
    roundRect(ctx, top.x - tagWidth / 2, top.y - rr - 36, tagWidth, 26, 13);
    ctx.fill();
    ctx.fillStyle = '#f9f4ed';
    ctx.fillText('You', top.x, top.y - rr - 22.5);
  };

  const drawPopup = (ctx, { trip, landT, fade, anchor }) => {
    const k = Math.min(1, Math.max(0, (landT - 120) / 300));
    const alpha = k * fade;
    if (alpha <= 0) return;
    const rise = (1 - k) * 8;
    ctx.font = '700 14px Figtree, sans-serif';
    const cw = Math.max(200, ctx.measureText(trip.d.service).width + 112);
    const ch = 58;
    const x = Math.max(12, Math.min(scene.w - cw - 12, anchor.x - cw / 2));
    // On wide screens the header sits over the top of the map; keep cards below it.
    const y = Math.max(scene.narrow ? 8 : 92, anchor.y - 14 - ch + rise);
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.shadowColor = 'rgba(46,43,37,0.2)'; ctx.shadowBlur = 18; ctx.shadowOffsetY = 5;
    ctx.fillStyle = '#ffffff';
    roundRect(ctx, x, y, cw, ch, 14);
    ctx.fill();
    ctx.shadowColor = 'transparent';
    const tx = Math.max(x + 18, Math.min(x + cw - 18, anchor.x));
    ctx.beginPath(); ctx.moveTo(tx - 7, y + ch - 0.5); ctx.lineTo(tx, y + ch + 7); ctx.lineTo(tx + 7, y + ch - 0.5); ctx.fill();
    ctx.fillStyle = '#e3e8cd';
    ctx.beginPath(); ctx.arc(x + 26, y + ch / 2, 14, 0, Math.PI * 2); ctx.fill();
    ctx.strokeStyle = '#5b6b43'; ctx.lineWidth = 2.4; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
    ctx.beginPath(); ctx.moveTo(x + 20, y + ch / 2); ctx.lineTo(x + 24.5, y + ch / 2 + 4.5); ctx.lineTo(x + 32, y + ch / 2 - 4); ctx.stroke();
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
    ctx.fillStyle = '#5b6b43'; ctx.font = '700 10px Figtree, sans-serif';
    ctx.fillText('LOCAL TALENT PREVIEW', x + 50, y + 24);
    ctx.fillStyle = '#201e1d'; ctx.font = '700 14px Figtree, sans-serif';
    ctx.fillText(trip.d.service, x + 50, y + 42);
    ctx.fillStyle = '#82796a'; ctx.font = '600 12px Figtree, sans-serif'; ctx.textAlign = 'right';
    ctx.fillText(trip.d.dist, x + cw - 14, y + 42);
    ctx.restore();
  };

  const frame = (now, { animate }) => {
    const ctx = canvas.getContext('2d');
    if (!ctx || !scene.staticCanvas) return;
    const { w, h, dpr } = scene;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.drawImage(scene.staticCanvas, 0, 0, w, h);
    const maxTrips = scene.narrow ? MAX_TRIPS_STACKED : MAX_TRIPS;
    if (animate && now - scene.lastLaunch > 1300 / SPEED && scene.trips.length < maxTrips) {
      launch(now);
      scene.lastLaunch = now;
    }

    for (let k = 0; k < 3; k += 1) {
      const rp = (now / 2400 + k / 3) % 1;
      groundRing(ctx, 0, 0, 40 + rp * 170, `rgba(113,128,79,${(1 - rp) * 0.55})`, 1.6);
    }
    scene.dests.forEach((d) => {
      if (d.busy) return;
      const p = proj(d.x, d.y);
      ctx.fillStyle = 'rgba(168,82,50,0.35)';
      ctx.beginPath(); ctx.arc(p.x, p.y, 3.2 * p.s, 0, Math.PI * 2); ctx.fill();
    });

    const HOLD = 2800 / SPEED;
    const FADE = 600;
    scene.trips = scene.trips.filter((t) => {
      const done = now - t.start > t.dur + HOLD + FADE;
      if (done) t.d.busy = false;
      return !done;
    });

    const popups = [];
    scene.trips.forEach((t) => {
      const elapsed = now - t.start;
      const p = Math.min(1, elapsed / t.dur);
      const eased = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2;
      const fade = elapsed > t.dur + HOLD ? Math.max(0, 1 - (elapsed - t.dur - HOLD) / FADE) : 1;
      const D = t.d;
      const L = Math.hypot(D.x, D.y);
      const H = Math.min(90, 20 + L * 0.1);
      const nx = -D.y / L;
      const ny = D.x / L;
      const at = (u) => {
        const side = Math.sin(Math.PI * u) * L * t.side;
        return [D.x * u + nx * side, D.y * u + ny * side, Math.sin(Math.PI * u) * H + (1 - u) * PIN_HEIGHT];
      };
      const n = Math.max(10, Math.floor(L / 20));
      for (let i = 1; i <= n; i += 1) {
        const u = i / n;
        if (u > eased) break;
        const [gx, gy, gz] = at(u);
        const shadow = proj(gx, gy, 0);
        ctx.fillStyle = `rgba(100,84,64,${0.13 * fade})`;
        ctx.beginPath(); ctx.arc(shadow.x, shadow.y, 1.7 * shadow.s, 0, Math.PI * 2); ctx.fill();
        const q = proj(gx, gy, gz);
        const head = p < 1 && eased - u < 1 / n;
        ctx.fillStyle = `rgba(168,82,50,${(head ? 1 : 0.8) * fade})`;
        ctx.beginPath(); ctx.arc(q.x, q.y, (head ? 5 : 2.8) * q.s, 0, Math.PI * 2); ctx.fill();
      }
      const landT = elapsed - t.dur;
      if (landT < 0) return;
      const ring = (landT % 1500) / 1500;
      groundRing(ctx, D.x, D.y, 10 + ring * 46, `rgba(168,82,50,${(1 - ring) * 0.6 * fade})`, 1.6);
      const gp = proj(D.x, D.y);
      const pop = Math.min(1, landT / 380);
      const scale = (pop < 1 ? 0.4 + 0.6 * (1 - Math.pow(1 - pop, 3)) * (1 + Math.sin(pop * Math.PI) * 0.25) : 1) * Math.max(0.8, gp.s);
      ctx.save();
      ctx.globalAlpha = fade;
      ctx.translate(gp.x, gp.y);
      ctx.scale(scale, scale);
      ctx.fillStyle = 'rgba(46,43,37,0.2)';
      ctx.beginPath(); ctx.ellipse(0, 1, 8, 3, 0, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = '#a85232';
      ctx.beginPath(); ctx.arc(0, -22, 13, Math.PI * 0.8, Math.PI * 2.2); ctx.lineTo(0, 0); ctx.closePath(); ctx.fill();
      ctx.fillStyle = '#f7efe4';
      ctx.beginPath(); ctx.arc(0, -22, 5, 0, Math.PI * 2); ctx.fill();
      ctx.restore();
      popups.push({ trip: t, landT, fade, anchor: { x: gp.x, y: gp.y - 30 * scale } });
    });

    drawYouPin(ctx);
    popups.forEach((popup) => drawPopup(ctx, popup));
  };

  return { resize, frame };
};

const DiscoveryMap = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || typeof canvas.getContext !== 'function' || !canvas.getContext('2d')) return undefined;

    const scene = createScene(canvas);
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
    let raf = 0;
    let visible = true;

    const loop = (now) => {
      scene.frame(now, { animate: true });
      raf = window.requestAnimationFrame(loop);
    };
    const start = () => {
      if (reduceMotion || raf || !visible) return;
      raf = window.requestAnimationFrame(loop);
    };
    const stop = () => {
      window.cancelAnimationFrame(raf);
      raf = 0;
    };
    // With reduced motion the map is one still frame: the city and the pin, no trips.
    const redraw = () => {
      if (scene.resize() && reduceMotion) scene.frame(0, { animate: false });
    };

    redraw();
    start();
    // The still frame's "You" tag is drawn in Figtree; repaint once the font arrives.
    if (reduceMotion) document.fonts?.ready?.then(() => scene.frame(0, { animate: false }));

    const resizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(redraw) : null;
    if (resizeObserver) resizeObserver.observe(canvas);
    else window.addEventListener('resize', redraw);

    // Stop drawing while the hero is scrolled out of view.
    const intersectionObserver = typeof IntersectionObserver === 'function'
      ? new IntersectionObserver(([entry]) => {
        visible = entry.isIntersecting;
        if (visible) start(); else stop();
      })
      : null;
    intersectionObserver?.observe(canvas);

    return () => {
      stop();
      resizeObserver?.disconnect();
      if (!resizeObserver) window.removeEventListener('resize', redraw);
      intersectionObserver?.disconnect();
    };
  }, []);

  return <canvas ref={canvasRef} className="discovery-map" aria-hidden="true" />;
};

export default DiscoveryMap;
